import { useState, useRef, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Upload,
  FileText,
  Sparkles,
  Loader2,
  UserPlus,
  CheckCircle2,
  Copy,
  ClipboardPaste,
} from "lucide-react";

interface Plan {
  id: string;
  name: string;
  price_cents: number;
  is_free: boolean;
}

interface ParsedData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job_title: string;
  industry: string;
  location: string;
  years_experience: number | null;
  linkedin_url: string;
}

interface PlatformResult {
  ok: boolean;
  skipped?: boolean;
  error?: string;
}

interface CreatedUser {
  userId: string;
  email: string;
  password: string;
  displayName: string;
  platforms?: Record<string, PlatformResult>;
}

/** JobIntel 360 membership tiers (mirrors the JobIntel project's plan keys). */
const JOBINTEL_PLANS = [
  { key: "trial", label: "Trial (default)" },
  { key: "seeker", label: "Seeker" },
  { key: "pro", label: "Pro" },
  { key: "executive", label: "Executive" },
  { key: "elite", label: "Elite" },
];

/** JobGPT (tryjobgpt.com) plans. Same login as JobIntel 360; the plan sits in
 *  its own JobGPT slot. Pro ($100/mo) is the quick-add default. */
const JOBGPT_PLANS = [
  { key: "jobgpt_starter", label: "Starter ($8/mo)" },
  { key: "jobgpt_plus", label: "Plus ($20/mo)" },
  { key: "jobgpt_pro", label: "Pro ($100/mo)" },
  { key: "jobgpt_max", label: "Max ($200/mo)" },
];

/** Academy courses whose checkboxes start CHECKED when the live list loads
 *  (the two standard masterclasses every new member gets by default). */
const ACADEMY_DEFAULT_CHECKED = [
  "bd3c076f-aead-4b21-97d5-90849027bac9", // The Science of Getting Job Interviews Masterclass 2026
  "1de8beb3-38c1-4da2-ae72-6e312565015d", // Executive Jobs on Demand: Mini Masterclass Series 2026
];

interface AcademyCourse {
  id: string;
  title: string;
  status: string;
}

interface AdminQuickOnboardProps {
  open: boolean;
  onClose: () => void;
  onUserCreated: () => void;
}

export function AdminQuickOnboard({
  open,
  onClose,
  onUserCreated,
}: AdminQuickOnboardProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Steps: "upload" -> "review" -> "success"
  const [step, setStep] = useState<"upload" | "review" | "success">("upload");
  const [isParsing, setIsParsing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [pastedText, setPastedText] = useState("");
  const [uploadedFileName, setUploadedFileName] = useState("");

  // Plans
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");

  // Cross-platform quick-add options
  const [twEnabled, setTwEnabled] = useState(true);
  const [jiEnabled, setJiEnabled] = useState(true);
  const [jiPlan, setJiPlan] = useState("trial");
  const [jgEnabled, setJgEnabled] = useState(true);
  const [jgPlan, setJgPlan] = useState("jobgpt_pro");
  const [acEnabled, setAcEnabled] = useState(true);
  const [acCourseIds, setAcCourseIds] = useState<string[]>(ACADEMY_DEFAULT_CHECKED);
  const [acCourses, setAcCourses] = useState<AcademyCourse[]>([]);
  const [acCoursesError, setAcCoursesError] = useState<string | null>(null);
  const [acCoursesLoading, setAcCoursesLoading] = useState(false);

  // Parsed / editable fields
  const [formData, setFormData] = useState<ParsedData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    job_title: "",
    industry: "",
    location: "",
    years_experience: null,
    linkedin_url: "",
  });

  const [createdUser, setCreatedUser] = useState<CreatedUser | null>(null);

  // Load plans on open
  const loadPlans = useCallback(async () => {
    const { data } = await supabase
      .from("subscription_plans")
      .select("id, name, price_cents, is_free")
      .eq("is_active", true)
      .order("price_cents", { ascending: true });

    if (data) {
      setPlans(data as Plan[]);
      // Default to Pro plan or the most popular one
      const proPlan = data.find((p: any) => p.name === "Pro");
      const defaultPlan = proPlan || data.find((p: any) => !p.is_free) || data[0];
      if (defaultPlan) setSelectedPlanId((defaultPlan as any).id);
    }
  }, []);

  // Live Academy course catalog (proxied through the edge function that holds
  // the provision secret). No hardcoded fallback: if the Academy can't be
  // reached we say so and let Marcus retry.
  const loadAcademyCourses = useCallback(async () => {
    setAcCoursesLoading(true);
    setAcCoursesError(null);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { action: "quick_onboard_courses" },
      });
      if (error) throw error;
      if (!data?.courses) throw new Error(data?.error || "Academy returned no courses");
      const courses = data.courses as AcademyCourse[];
      setAcCourses(courses);
      // Keep only selections that exist in the live catalog; default-check the
      // two standard masterclasses when present.
      setAcCourseIds((prev) => {
        const live = new Set(courses.map((c) => c.id));
        const kept = prev.filter((id) => live.has(id));
        return kept.length ? kept : ACADEMY_DEFAULT_CHECKED.filter((id) => live.has(id));
      });
    } catch (e: any) {
      setAcCourses([]);
      setAcCoursesError(e?.message || "Could not load courses from Jobs on Demand Academy");
    } finally {
      setAcCoursesLoading(false);
    }
  }, []);

  // Load plans + Academy catalog when dialog opens
  useEffect(() => {
    if (open) {
      loadPlans();
      loadAcademyCourses();
    }
  }, [open, loadPlans, loadAcademyCourses]);

  const extractTextFromFile = async (file: File): Promise<string> => {
    // Use the exact same DocumentProcessor utility the resume tailoring
    // page and DocumentUpload component rely on. It handles:
    //   • PDFs   → uploads to Supabase Storage, runs `scrape-job-url`
    //              (Firecrawl) to pull every line of text
    //   • Word   → mammoth.extractRawText
    // This avoids the broken `extract-pdf-text` stub the old code called.
    const { DocumentProcessor } = await import("@/utils/documentProcessor");
    const fileType = file.type;

    if (fileType === "application/pdf"
        || fileType === "application/msword"
        || fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      const result = await DocumentProcessor.processDocument(file);
      if (!result?.text) throw new Error("No text could be extracted from the document.");
      return result.text;
    }

    // Plain text fallback for .txt / unknown
    return await file.text();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFileName(file.name);
    setIsParsing(true);

    try {
      const text = await extractTextFromFile(file);
      if (!text.trim()) {
        toast({
          title: "Could not extract text",
          description: "The file appears to be empty or unreadable.",
          variant: "destructive",
        });
        setIsParsing(false);
        return;
      }

      await parseResumeText(text);
    } catch (error: any) {
      console.error("File processing error:", error);
      // Surface the actual error message so we can see whether it's a
      // storage / ConvertAPI / auth / network failure.
      const detail = error?.message || (typeof error === 'string' ? error : 'Unknown error');
      toast({
        title: "File processing failed",
        description: `Could not read the uploaded file — ${detail}`,
        variant: "destructive",
      });
      setIsParsing(false);
    }
  };

  const handlePasteAndParse = async () => {
    if (!pastedText.trim()) {
      toast({
        title: "No text provided",
        description: "Please paste resume text first.",
        variant: "destructive",
      });
      return;
    }

    setIsParsing(true);
    await parseResumeText(pastedText);
  };

  const parseResumeText = async (text: string) => {
    try {
      const { data, error } = await supabase.functions.invoke(
        "delete-user",
        {
          body: { action: "quick_onboard_parse", resumeText: text },
        }
      );

      if (error) throw error;

      if (data?.parsed) {
        setFormData({
          first_name: data.parsed.first_name || "",
          last_name: data.parsed.last_name || "",
          email: data.parsed.email || "",
          phone: data.parsed.phone || "",
          job_title: data.parsed.job_title || "",
          industry: data.parsed.industry || "",
          location: data.parsed.location || "",
          years_experience: data.parsed.years_experience || null,
          linkedin_url: data.parsed.linkedin_url || "",
        });
        setStep("review");
        toast({
          title: "Resume parsed!",
          description: "Review the extracted information below.",
        });
      }
    } catch (error) {
      console.error("Parse error:", error);
      toast({
        title: "Parsing failed",
        description: "AI could not extract data. Please try pasting text instead.",
        variant: "destructive",
      });
    } finally {
      setIsParsing(false);
    }
  };

  const generatePassword = () => {
    const phoneDigits = formData.phone.replace(/\D/g, "");
    const last4 = phoneDigits.slice(-4);
    return formData.last_name.toLowerCase() + last4;
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.last_name) {
      toast({
        title: "Missing required fields",
        description: "Email and Last Name are required.",
        variant: "destructive",
      });
      return;
    }

    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (phoneDigits.length < 4) {
      toast({
        title: "Phone number too short",
        description: "Phone number must have at least 4 digits for password generation.",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "delete-user",
        {
          body: {
            action: "quick_onboard_create",
            userData: formData,
            planId: selectedPlanId,
            platforms: {
              tailorwiz: { enabled: twEnabled },
              jobintel: { enabled: jiEnabled, plan: jiPlan },
              jobgpt: { enabled: jgEnabled, plan: jgPlan },
              academy: { enabled: acEnabled, courseIds: acCourseIds },
            },
          },
        }
      );

      if (error) throw error;

      if (data?.error) {
        toast({
          title: "Creation failed",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      if (data?.success) {
        setCreatedUser({
          userId: data.userId,
          email: data.email,
          password: data.password,
          displayName: data.displayName,
          platforms: data.platforms,
        });
        setStep("success");
        onUserCreated();
        toast({ title: "User created successfully!" });
      }
    } catch (error) {
      console.error("Create error:", error);
      toast({
        title: "Failed to create user",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied to clipboard!" });
  };

  const handleClose = () => {
    setStep("upload");
    setPastedText("");
    setUploadedFileName("");
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      job_title: "",
      industry: "",
      location: "",
      years_experience: null,
      linkedin_url: "",
    });
    setCreatedUser(null);
    setTwEnabled(true);
    setJiEnabled(true);
    setJiPlan("trial");
    setJgEnabled(true);
    setJgPlan("jobgpt_pro");
    setAcEnabled(true);
    setAcCourseIds(ACADEMY_DEFAULT_CHECKED.filter((id) => acCourses.some((c) => c.id === id)));
    onClose();
  };

  const updateField = (field: keyof ParsedData, value: string | number | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" />
            Quick Onboard New User
          </DialogTitle>
          <DialogDescription>
            {step === "upload" && "Upload a resume or paste text to auto-create a new user."}
            {step === "review" && "Review and edit the extracted information, then create the user."}
            {step === "success" && "User created successfully! Share the credentials below."}
          </DialogDescription>
        </DialogHeader>

        {/* ── STEP 1: UPLOAD ── */}
        {step === "upload" && (
          <div className="space-y-6">
            {/* File Upload */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Upload Resume (PDF or Word)</Label>
              <div
                className="border-2 border-dashed border-primary/30 rounded-xl p-8 text-center cursor-pointer hover:border-primary/60 hover:bg-primary/5 transition-all"
                onClick={() => fileInputRef.current?.click()}
              >
                {isParsing ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">
                      Parsing resume with AI...
                    </p>
                  </div>
                ) : uploadedFileName ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileText className="h-8 w-8 text-primary" />
                    <p className="font-medium">{uploadedFileName}</p>
                    <p className="text-xs text-muted-foreground">Click to change file</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="h-8 w-8 text-muted-foreground" />
                    <p className="font-medium">Drop a resume here or click to browse</p>
                    <p className="text-xs text-muted-foreground">
                      Supports PDF, DOCX, DOC, TXT
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.txt"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium">OR</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Paste Text */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <ClipboardPaste className="h-4 w-4" />
                Paste Resume Text
              </Label>
              <Textarea
                placeholder="Paste the full resume text here..."
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                className="min-h-[200px] font-mono text-xs"
              />
              <Button
                onClick={handlePasteAndParse}
                disabled={!pastedText.trim() || isParsing}
                className="w-full"
              >
                {isParsing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Parse with AI
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 2: REVIEW ── */}
        {step === "review" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => updateField("first_name", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => updateField("last_name", e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Email (Username) *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Phone * (last 4 digits used for password)</Label>
              <Input
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="e.g. 5551234567"
              />
            </div>

            {/* Password preview */}
            {formData.last_name && formData.phone.replace(/\D/g, "").length >= 4 && (
              <div className="bg-muted/50 border rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">Generated Password:</p>
                <p className="font-mono font-semibold text-sm">{generatePassword()}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Job Title</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => updateField("job_title", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Industry</Label>
                <Input
                  value={formData.industry}
                  onChange={(e) => updateField("industry", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => updateField("location", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Years Experience</Label>
                <Input
                  type="number"
                  value={formData.years_experience ?? ""}
                  onChange={(e) =>
                    updateField(
                      "years_experience",
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">LinkedIn URL</Label>
              <Input
                value={formData.linkedin_url}
                onChange={(e) => updateField("linkedin_url", e.target.value)}
              />
            </div>

            {/* ── Platform access — one login created across all three ── */}
            <div className="space-y-3 rounded-lg border p-3">
              <Label className="text-sm font-semibold">Platform access (same login on each)</Label>

              {/* TailorWiz */}
              <div className="flex items-center gap-3">
                <Checkbox checked={twEnabled} onCheckedChange={(v) => setTwEnabled(!!v)} id="tw-en" />
                <Label htmlFor="tw-en" className="w-28 shrink-0 text-sm">TailorWiz</Label>
                <Select value={selectedPlanId} onValueChange={setSelectedPlanId} disabled={!twEnabled}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} {plan.price_cents > 0 ? `($${(plan.price_cents / 100).toFixed(0)}/mo)` : "(Free)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* JobIntel 360 */}
              <div className="flex items-center gap-3">
                <Checkbox checked={jiEnabled} onCheckedChange={(v) => setJiEnabled(!!v)} id="ji-en" />
                <Label htmlFor="ji-en" className="w-28 shrink-0 text-sm">JobIntel 360</Label>
                <Select value={jiPlan} onValueChange={setJiPlan} disabled={!jiEnabled}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent>
                    {JOBINTEL_PLANS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* JobGPT (tryjobgpt.com) */}
              <div className="flex items-center gap-3">
                <Checkbox checked={jgEnabled} onCheckedChange={(v) => setJgEnabled(!!v)} id="jg-en" />
                <Label htmlFor="jg-en" className="w-28 shrink-0 text-sm">JobGPT</Label>
                <Select value={jgPlan} onValueChange={setJgPlan} disabled={!jgEnabled}>
                  <SelectTrigger className="flex-1"><SelectValue placeholder="Select plan" /></SelectTrigger>
                  <SelectContent>
                    {JOBGPT_PLANS.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Jobs on Demand Academy (CourseMagic) */}
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Checkbox checked={acEnabled} onCheckedChange={(v) => setAcEnabled(!!v)} id="ac-en" />
                  <Label htmlFor="ac-en" className="text-sm">Jobs on Demand Academy</Label>
                </div>
                {acEnabled && (
                  <div className="ml-7 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground">
                        Courses to grant ({acCourseIds.length} selected):
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="text-[11px] text-blue-600 hover:underline"
                          onClick={() => setAcCourseIds(acCourses.map((c) => c.id))}
                          disabled={!acCourses.length}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="text-[11px] text-blue-600 hover:underline"
                          onClick={() => setAcCourseIds([])}
                          disabled={!acCourses.length}
                        >
                          None
                        </button>
                      </div>
                    </div>
                    {acCoursesLoading && (
                      <p className="text-xs text-muted-foreground">Loading courses from the Academy…</p>
                    )}
                    {acCoursesError && (
                      <div className="text-xs text-red-600 space-y-1">
                        <p>{acCoursesError}</p>
                        <button type="button" className="text-blue-600 hover:underline" onClick={loadAcademyCourses}>
                          Retry
                        </button>
                      </div>
                    )}
                    <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                      {acCourses.map((course) => {
                        const checked = acCourseIds.includes(course.id);
                        return (
                          <div key={course.id} className="flex items-start gap-2">
                            <Checkbox
                              id={`ac-${course.id}`}
                              checked={checked}
                              onCheckedChange={(v) =>
                                setAcCourseIds((prev) =>
                                  v ? [...new Set([...prev, course.id])] : prev.filter((x) => x !== course.id)
                                )
                              }
                            />
                            <Label htmlFor={`ac-${course.id}`} className="text-xs leading-snug">
                              {course.title}
                              {course.status?.toLowerCase() !== "published" && (
                                <span className="ml-1 text-[10px] text-amber-600">({course.status})</span>
                              )}
                            </Label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep("upload")}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={isCreating}
                className="flex-1"
              >
                {isCreating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserPlus className="h-4 w-4 mr-2" />
                )}
                Create User
              </Button>
            </div>
          </div>
        )}

        {/* ── STEP 3: SUCCESS ── */}
        {step === "success" && createdUser && (
          <div className="space-y-6 text-center">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-success/20 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-success" />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold">{createdUser.displayName}</h3>
              <p className="text-muted-foreground">Account created successfully</p>
            </div>

            <div className="bg-muted/50 border rounded-lg p-4 text-left space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Email / Username</p>
                  <p className="font-mono text-sm">{createdUser.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(createdUser.email)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Password</p>
                  <p className="font-mono text-sm font-semibold">
                    {createdUser.password}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(createdUser.password)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Per-platform results */}
            {createdUser.platforms && (
              <div className="border rounded-lg p-3 text-left space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">Platforms</p>
                {[
                  { key: "tailorwiz", label: "TailorWiz" },
                  { key: "jobintel", label: "JobIntel 360" },
                  { key: "jobgpt", label: "JobGPT" },
                  { key: "academy", label: "Jobs on Demand Academy" },
                ].map(({ key, label }) => {
                  const r = createdUser.platforms?.[key];
                  const status = !r ? "—" : r.ok ? "Created" : r.skipped ? "Skipped" : "Failed";
                  const color = !r ? "text-muted-foreground" : r.ok ? "text-success" : r.skipped ? "text-muted-foreground" : "text-destructive";
                  return (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span>{label}</span>
                      <span className={`font-semibold ${color}`} title={r?.error ?? ""}>{status}</span>
                    </div>
                  );
                })}
                {Object.values(createdUser.platforms).some((r) => r && !r.ok && !r.skipped) && (
                  <p className="text-[11px] text-destructive leading-snug pt-1">
                    Some platforms failed (hover the status for details). The created accounts above still work; re-run for the failed ones once configured.
                  </p>
                )}
              </div>
            )}

            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
